import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ElementType, type ReactNode } from 'react';

const Base = chakra('p', {
  base: { fontFamily: 'body', margin: 0, color: 'text.primary' },
});

type TextSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type TextTone = 'primary' | 'secondary' | 'muted' | 'inverse' | 'brand' | 'danger' | 'success';
type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';

const toneColor: Record<TextTone, string> = {
  primary: 'text.primary',
  secondary: 'text.secondary',
  muted: 'text.muted',
  inverse: 'text.inverse',
  brand: 'brand.fg',
  danger: 'status.danger',
  success: 'status.success',
};

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  as?: ElementType;
  size?: TextSize;
  tone?: TextTone;
  weight?: TextWeight;
  /** Use the monospace family (ids, tokens, JSON). */
  mono?: boolean;
  /** Truncate to a single line with an ellipsis. */
  truncate?: boolean;
  align?: 'start' | 'center' | 'end';
  children?: ReactNode;
}

/** Body / UI text. Token-driven size, tone and weight; hides Chakra style props. */
export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { as, size = 'md', tone = 'primary', weight = 'regular', mono, truncate, align, ...rest },
  ref,
) {
  const sx: HTMLChakraProps<'p'> = {
    fontSize: size,
    color: toneColor[tone],
    fontWeight: weight,
    fontFamily: mono ? 'mono' : 'body',
    lineHeight: 'normal',
    textAlign: align,
    ...(truncate ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
  };
  return <Base as={as} ref={ref} {...sx} {...rest} />;
});
